import React, { useState, useEffect, Fragment } from 'react';
import { useBranch } from '../../../layouts/BranchContext'; 
import {
  Button,
  Container,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Modal,
  Snackbar,
  Tooltip,
  useTheme,
  Chip,
  ListSubheader,
  Badge,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const accessToken = localStorage.getItem('accessToken');
const getImageUrl = (image) => {
  if (!image) return '';

  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }

  return `${API_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
};
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditAdmission = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

    const { branchList: branches } = useBranch();

  // State
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);

  // States for dropdowns
  const [registrations, setRegistrations] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');

  // Add these new states with your existing state declarations
  const [batches, setBatches] = useState([]);
  const [batchesLoaded, setBatchesLoaded] = useState(false); // guard flag
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [studentImage, setStudentImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown open state (same as AddAdmission)
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);

  const [formData, setFormData] = useState({
    sendEmail: false,
    sendTestSMS: false,
    direct: false,
    cancelAdmission: false,
    cancellationDate: '',
    reason: '',
    admissionCode: '',
    admissionDate: new Date().toISOString().split('T')[0],
    candidateName: '',
    mobileNo: '',
    alternateMobileNo: '',
    gender: '',
    email: '',
    address: '',
    status: '',
    fatherName: '',
    motherName: '',
    qualification: '',
    aadhaarNo: '',
    registration: '',
    organization: '',
  });

  // Fetch Admission Details
  useEffect(() => {
    console.log('Admission ID:', id);
    console.log('Access Token:', accessToken);

const fetchAdmissionDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admission/admissions/${id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = response.data;
      console.log('Admission Details:', data);

      setFormData({
        sendEmail: data.send_email || false,
        sendTestSMS: data.send_text_sms || false,
        direct: data.direct || false,
        cancelAdmission: data.cancel_admission || false,
        cancellationDate: data.cancellation_date || '',
        reason: data.reason || '',
        admissionCode: data.admission_code || '',
        admissionDate: data.admission_date || new Date().toISOString().split('T')[0],
        candidateName: data.candidate_name || '',
        mobileNo: data.mobile_no || '',
        alternateMobileNo: data.alternate_mobile_no || '',
        gender: data.gender || '',
        email: data.email || '',
        address: data.address || '',
        status: data.status || '',
        fatherName: data.father_name || '',
        motherName: data.mother_name || '',
        qualification: data.qualification || '',
        aadhaarNo: data.aadhaar_no || '',
        registration: data.registration || '',
        organization: data.organization || '',
        branch: data.branch || '',
      });

      setSelectedRegistration(data.registration || '');
      setSelectedOrganization(data.organization || '');

      // Map courses - new API uses course_id instead of id
      if (data.courses && Array.isArray(data.courses)) {
        const courseIds = data.courses.map(course => course.course_id);
        setSelectedCourses(courseIds);

        // Batches are nested inside each course object (not a top-level array)
        // Flatten all batches from all courses and extract batch_id
        const batchIds = data.courses.flatMap(course =>
          (course.batches || []).map(batch => batch.batch_id)
        );
        setSelectedBatches(batchIds);
      }

      // Map image directly from the response
     setImagePreview(getImageUrl(data.image));

    } catch (error) {
      console.error('Error fetching admission details:', error);
      setSnackbarMessage('Failed to load admission details.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

    if (id) {
      fetchAdmissionDetails();
    }
  }, [id]);

  // Static Data
  const staticGenderData = [
    { id: 1, name: 'Male' },
    { id: 2, name: 'Female' },
    { id: 3, name: 'Other' },
  ];

  const staticStatusData = [
    { id: 1, name: 'Admitted' },
    { id: 2, name: 'Cancelled' },
  ];

 

  // Fetch Batches
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/course/batches/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setBatches(response.data);
        setBatchesLoaded(true); // mark batches as fully loaded
      } catch (error) {
        console.error('Error fetching batches:', error);
        setSnackbarMessage('Failed to load batches');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    };
    fetchBatches();
  }, []);

  // Auto-select organization from session storage
useEffect(() => {
  try {
    const sessionUser = JSON.parse(sessionStorage.getItem('user')) || {};
    const orgId = sessionUser.organization_id;
    if (orgId) {
      setSelectedOrganization(orgId);
      setFormData(prev => ({ ...prev, organization: orgId }));
    }
  } catch (e) {}
}, []);
// Auto-select branch if only one is available for the selected organization
useEffect(() => {
  if (selectedOrganization && branches.length > 0) {
    const filteredBranchesForOrg = branches.filter(branch => 
      branch.organization === parseInt(selectedOrganization)
    );
    
    // Auto-select branch if only one is available and no branch selected
    if (filteredBranchesForOrg.length === 1 && !formData.branch) {
      setFormData(prev => ({
        ...prev,
        branch: filteredBranchesForOrg[0].id
      }));
    }
  }
}, [selectedOrganization, branches, formData.branch]);


  // Fetch Courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/course/courses/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        console.log('Courses:', response.data);
        setCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };
    fetchCourses();
  }, []);

  // Filter courses based on selected organization and branch
useEffect(() => {
  if (courses.length === 0) return;
  
  let filtered = [...courses];
  
  // Filter by organization
  if (selectedOrganization) {
    filtered = filtered.filter(course => 
      course.organization === parseInt(selectedOrganization)
    );
  }
  
  // Filter by branch
  if (formData.branch) {
    filtered = filtered.filter(course => 
      course.branch === parseInt(formData.branch)
    );
  }
  
  setFilteredCourses(filtered);
  
  // Clear selected courses that are no longer in filtered list
  const validSelectedCourses = selectedCourses.filter(courseId =>
    filtered.some(course => course.id === courseId)
  );
  
  if (validSelectedCourses.length !== selectedCourses.length) {
    setSelectedCourses(validSelectedCourses);
    // Also clear batches when courses are cleared
    if (validSelectedCourses.length === 0) {
      setSelectedBatches([]);
    }
  }
  
}, [selectedOrganization, formData.branch, courses]);

 // Fetch Registrations filtered by branch
useEffect(() => {
  if (!formData.branch) return;

  const fetchRegistrations = async () => {
    try {
      const sessionUser = JSON.parse(sessionStorage.getItem('user')) || {};
      const orgId = sessionUser.organization_id;

      const params = new URLSearchParams();
      if (orgId) params.append('organization', orgId);
      if (formData.branch) params.append('branch', formData.branch);

      const response = await axios.get(
        `${API_BASE_URL}/api/student_details/registrations/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  fetchRegistrations();
}, [formData.branch]);

  // Fetch Organizations (only GET, no PUT)
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/master/organizations/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        console.log('Organizations:', response.data);
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, []);

  // Filter batches based on selected courses (same as AddAdmission)
  useEffect(() => {
    if (selectedCourses.length === 0) {
      setFilteredBatches([]);
      // Do NOT clear selectedBatches — pre-loaded values must survive
      // the initial render before courses arrive from the API.
    } else {
      const filtered = batches.filter(batch =>
        selectedCourses.includes(batch.course)
      );
      setFilteredBatches(filtered);

      // Only trim invalid selectedBatches AFTER batches have fully loaded.
      // If we do this before load, filtered is [] and wipes pre-loaded IDs.
      if (batchesLoaded && filtered.length > 0) {
        const validSelectedBatches = selectedBatches.filter(batchId =>
          filtered.some(batch => batch.id === batchId)
        );
        if (validSelectedBatches.length !== selectedBatches.length) {
          setSelectedBatches(validSelectedBatches);
        }
      }
    }
  }, [selectedCourses, batches, batchesLoaded]);

  // Handlers
const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value,
  }));

  // Agar cancelAdmission unchecked karte hain to cancellationDate aur reason clear karo
  if (name === 'cancelAdmission' && !checked) {
    setFormData(prev => ({
      ...prev,
      cancellationDate: '',
      reason: ''
    }));
  }
};

  const handleCourseChange = (event) => {
    const { value } = event.target;
    setSelectedCourses(typeof value === 'string' ? value.split(',') : value);
  };

  const handleBatchChange = (event) => {
    const value = event.target.value;
    // Ensure value is always an array
    const newValue = Array.isArray(value) ? value : [value];
    setSelectedBatches(newValue);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic client-side validation
    if (!file.type.startsWith('image/')) {
      setSnackbarMessage('Please select an image file (jpg, png, etc.)');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      setSnackbarMessage('Image size should be less than 2MB');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    setStudentImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRegistrationChange = (event) => {
    const value = event.target.value;
    console.log('Selected Registration ID:', value);
    setSelectedRegistration(value);
    setFormData(prev => ({ ...prev, registration: value }));
  };

const handleOrganizationChange = (event) => {
  const value = event.target.value;
  console.log('Selected Organization ID:', value);
  setSelectedOrganization(value);
  setFormData(prev => ({ 
    ...prev, 
    organization: value,
    branch: '' // Reset branch when organization changes
  }));
  // Reset courses and batches when organization changes
  setSelectedCourses([]);
  setSelectedBatches([]);
};

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validation
    if (
      !formData.admissionCode ||
      !formData.candidateName ||
      !formData.mobileNo ||
      !formData.gender ||
      !formData.email ||
      !formData.address ||
      !formData.status ||
      !formData.branch || // Add branch validation
      selectedCourses.length === 0 ||
      selectedBatches.length === 0 // Add batch validation
    ) {
      setSnackbarMessage('Please fill all required fields and select at least one course and one batch!');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setIsSubmitting(false);
      return;
    }

    // Agar cancelAdmission true hai to cancellationDate aur reason required hain
    if (formData.cancelAdmission) {
      if (!formData.cancellationDate) {
        setSnackbarMessage('Please select cancellation date!');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setIsSubmitting(false);
        return;
      }
      if (!formData.reason) {
        setSnackbarMessage('Please enter cancellation reason!');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const formDataToSend = new FormData();

      // Append all fields
      formDataToSend.append('send_email', formData.sendEmail);
      formDataToSend.append('send_text_sms', formData.sendTestSMS);
      formDataToSend.append('direct', formData.direct);
      formDataToSend.append('cancel_admission', formData.cancelAdmission);

      if (formData.cancelAdmission) {
        formDataToSend.append('cancellation_date', formData.cancellationDate);
        formDataToSend.append('reason', formData.reason);
      }

      // Append image if new image selected
      if (studentImage) {
        formDataToSend.append('image', studentImage);
      }

      formDataToSend.append('registration', formData.registration || '');
      formDataToSend.append('organization', formData.organization || '');
      formDataToSend.append('branch', formData.branch); // Add branch
      formDataToSend.append('admission_date', formData.admissionDate);
      formDataToSend.append('candidate_name', formData.candidateName);
      formDataToSend.append('mobile_no', formData.mobileNo);
      formDataToSend.append('alternate_mobile_no', formData.alternateMobileNo || '');
      formDataToSend.append('gender', formData.gender);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('father_name', formData.fatherName || '');
      formDataToSend.append('mother_name', formData.motherName || '');
      formDataToSend.append('qualification', formData.qualification || '');
      formDataToSend.append('aadhaar_no', formData.aadhaarNo || '');

     // Append course_batches (backend expects one paired array, not two separate fields)
const courseBatchesPayload = selectedBatches.map((batchId) => {
  const batch = batches.find((b) => b.id === batchId);
  return {
    course: batch?.course,
    batch: batchId,
  };
});

formDataToSend.append('course_batches', JSON.stringify(courseBatchesPayload));

      console.log('Saving admission with FormData');

      const response = await axios.put(`${API_BASE_URL}/api/admission/admissions/${id}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
      });

     if (response.status === 200) {
      setShowSuccessModal(true);
    } else {
      throw new Error('Failed to update admission details.');
    }
  } catch (error) {
    console.error('Error updating admission:', error);
    
    // Extract error message from response
    let errorMessage = 'An error occurred while updating admission details.';
    
    if (error.response) {
      const responseData = error.response.data;
      
      if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData.detail) {
        errorMessage = responseData.detail;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      } else if (typeof responseData === 'object') {
        const firstKey = Object.keys(responseData)[0];
        if (firstKey) {
          const firstError = responseData[firstKey];
          if (Array.isArray(firstError)) {
            errorMessage = `${firstKey}: ${firstError.join(', ')}`;
          } else if (typeof firstError === 'string') {
            errorMessage = `${firstKey}: ${firstError}`;
          } else {
            errorMessage = JSON.stringify(responseData);
          }
        } else {
          errorMessage = JSON.stringify(responseData);
        }
      }
    } else if (error.request) {
      errorMessage = 'No response from server. Please check your network connection.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setSnackbarMessage(errorMessage);
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleCancel = () => {
    navigate(-1);
  };

  // Helper function to get course name by ID (same as AddAdmission)
  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.course_name || course?.name || `Course ${courseId}`;
  };

  // Get registration display name
  const getRegistrationDisplay = (reg) => {
    return reg ? `${reg.registration_code || 'REG'} - ${reg.candidate_name || ''}` : '';
  };

  // Group batches by course for better UI (same as AddAdmission)
  const getBatchesGroupedByCourse = () => {
    if (selectedCourses.length === 0) return {};

    const grouped = {};
    filteredBatches.forEach(batch => {
      const courseName = getCourseName(batch.course);
      if (!grouped[courseName]) {
        grouped[courseName] = [];
      }
      grouped[courseName].push(batch);
    });
    return grouped;
  };

  // UI
  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box sx={{ p: 4, borderRadius: 2, backgroundColor: theme.palette.background.paper }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 3 }}>
          Edit Admission Details
        </Typography>

        <form onSubmit={handleSave}>
          {/* Checkboxes */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={handleChange}
                  sx={{ color: theme.palette.text.primary, '&.Mui-checked': { color: theme.palette.primary.main } }}
                />
              }
              label="Send Email"
              sx={{ color: theme.palette.text.primary }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="sendTestSMS"
                  checked={formData.sendTestSMS}
                  onChange={handleChange}
                  sx={{ color: theme.palette.text.primary, '&.Mui-checked': { color: theme.palette.primary.main } }}
                />
              }
              label="Send Test SMS"
              sx={{ color: theme.palette.text.primary }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="direct"
                  checked={formData.direct}
                  onChange={handleChange}
                  sx={{ color: theme.palette.text.primary, '&.Mui-checked': { color: theme.palette.primary.main } }}
                />
              }
              label="Direct"
              sx={{ color: theme.palette.text.primary }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="cancelAdmission"
                  checked={formData.cancelAdmission}
                  onChange={handleChange}
                  sx={{ color: theme.palette.text.primary, '&.Mui-checked': { color: theme.palette.primary.main } }}
                />
              }
              label="Cancel Admission"
              sx={{ color: theme.palette.text.primary }}
            />
          </Box>

          {/* Student Photo */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: 'medium', color: theme.palette.text.primary }}
            >
              Student Photograph
            </Typography>

            <Box
              sx={{
                width: 160,
                height: 180,
                mx: 'auto',
                mb: 1.5,
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: theme.palette.action.hover,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Student preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography variant="body2" color="text.primary" sx={{ px: 3, textAlign: 'center' }}>
                  No photo selected
                </Typography>
              )}
            </Box>

            <input
              accept="image/*"
              type="file"
              id="student-photo-upload"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />

            <label htmlFor="student-photo-upload">
              <Button
                variant="outlined"
                size="small"
                component="span"
                sx={{ textTransform: 'none' }}
              >
                {imagePreview ? 'Change Photo' : 'Upload Photo'}
              </Button>
            </label>

            {imagePreview && (
              <Typography variant="caption" color="text.primary" sx={{ mt: 1, display: 'block' }}>
                Max 2MB • JPG / PNG recommended
              </Typography>
            )}
          </Box>

          {/* Conditional Fields - Now visible when cancelAdmission is checked */}
          {formData.cancelAdmission && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
                label="Cancellation Date"
                type="date"
                variant="outlined"
                fullWidth
                name="cancellationDate"
                value={formData.cancellationDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                  sx: { color: theme.palette.text.primary }
                }}
                required
                sx={{
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                }}
              />
              <TextField
                label="Reason"
                variant="outlined"
                fullWidth
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                multiline
                rows={2}
                required
                sx={{
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                }}
              />
            </Box>
          )}

{/* registration */}
        <TextField
  label="Registration"
  variant="outlined"
  fullWidth
  value={selectedRegistration ? getRegistrationDisplay(registrations.find(r => r.id === selectedRegistration)) : 'None'}
  disabled
  sx={{
    mb: 3,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
    },
    '&.Mui-disabled': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.action.disabled,
      },
    },
  }}
  helperText="Registration cannot be changed in edit mode"
/>

          {/* New Organization Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
  <InputLabel sx={{ color: theme.palette.text.primary }}>Organization *</InputLabel>
  <Select
    name="organization"
    value={selectedOrganization}
    label="Organization *"
    onChange={handleOrganizationChange}
    disabled={true}
    sx={{
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.text.primary,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.action.disabled,
        },
      },
    }}
  >
    <MenuItem value="">
      <em>None</em>
    </MenuItem>
    {organizations.map((org) => (
      <MenuItem key={org.id} value={org.id}>
        {org.name}
      </MenuItem>
    ))}
  </Select>
  
</FormControl>

{/* Branch Field */}
<FormControl
  fullWidth
  sx={{
    mb: 3,
    '& .MuiInputLabel-root': { color: theme.palette.text.primary },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  }}
  required
>
  <InputLabel>Branch *</InputLabel>
    <Select
    name="branch"
    value={formData.branch || ''}
    label="Branch *"
    onChange={handleChange}
    disabled
    sx={{
      '&.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.action.disabled,
        },
      },
    }}
  >
    <MenuItem value="">Select Branch</MenuItem>
    {branches
      .filter((branch) => !selectedOrganization || branch.organization === parseInt(selectedOrganization))
      .map((branch) => (
        <MenuItem key={branch.id} value={branch.id}>
          {branch.name || branch.branch_name || `Branch ${branch.id}`}
        </MenuItem>
      ))}
  </Select>
  {branches.filter(b => b.organization === parseInt(selectedOrganization)).length === 1 && selectedOrganization && (
    <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.info.main }}>
      Only one branch available for this organization, auto-selected
    </Typography>
  )}
</FormControl>


          {/* Required Fields */}
          <TextField
          disabled
            label="Admission Code"
            variant="outlined"
            fullWidth
            name="admissionCode"
            value={formData.admissionCode}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />

          {/* Admission Date - Fixed to show label properly */}
          <TextField
            label="Admission Date"
            type="date"
            variant="outlined"
            fullWidth
            name="admissionDate"
            value={formData.admissionDate}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
              sx: { color: theme.palette.text.primary }
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />

          <TextField
            label="Candidate Name"
            variant="outlined"
            fullWidth
            name="candidateName"
            value={formData.candidateName}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />
          <TextField
            label="Mobile No."
            variant="outlined"
            fullWidth
            name="mobileNo"
            value={formData.mobileNo}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />
          <TextField
            label="Alternate Mobile No."
            variant="outlined"
            fullWidth
            name="alternateMobileNo"
            value={formData.alternateMobileNo}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
          <FormControl fullWidth
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required>
            <InputLabel>Gender</InputLabel>
            <Select name="gender" value={formData.gender} label="Gender" onChange={handleChange}>
              {staticGenderData.map((gender) => (
                <MenuItem key={gender.id} value={gender.name}>
                  {gender.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />
          <TextField
            label="Address"
            variant="outlined"
            fullWidth
            name="address"
            value={formData.address}
            onChange={handleChange}
            multiline
            rows={2}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />
          <FormControl fullWidth
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required>
            <InputLabel>Status</InputLabel>
            <Select name="status" value={formData.status} label="Status" onChange={handleChange}>
              {staticStatusData.map((status) => (
                <MenuItem key={status.id} value={status.name}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Father Name"
            variant="outlined"
            fullWidth
            name="fatherName"
            value={formData.fatherName}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
          <TextField
            label="Mother Name"
            variant="outlined"
            fullWidth
            name="motherName"
            value={formData.motherName}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
          <TextField
            label="Qualification"
            variant="outlined"
            fullWidth
            name="qualification"
            value={formData.qualification}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
          <TextField
            label="Aadhaar No."
            variant="outlined"
            fullWidth
            name="aadhaarNo"
            value={formData.aadhaarNo}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />

         {/* Courses Multi-Select — filtered by organization and branch */}
<FormControl fullWidth sx={{ mb: 3 }} required>
  <InputLabel
    shrink={selectedCourses.length > 0}
    id="courses-label"
    sx={{ color: theme.palette.text.primary }}
  >
    Courses {(!selectedOrganization || !formData.branch) && "(Select organization and branch first)"}
  </InputLabel>
  <Select
    labelId="courses-label"
    multiple
    value={selectedCourses}
    onChange={handleCourseChange}
    label="Courses"
    disabled={!selectedOrganization || !formData.branch || filteredCourses.length === 0}
    open={courseDropdownOpen}
    onOpen={() => setCourseDropdownOpen(true)}
    onClose={() => setCourseDropdownOpen(false)}
    renderValue={(selected) => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => {
          const course = filteredCourses.find(c => c.id === value);
          return (
            <Chip
              key={value}
              label={course?.course_name || `Course ${value}`}
              size="small"
              onDelete={() => {
                setSelectedCourses(selectedCourses.filter(id => id !== value));
              }}
              deleteIcon={<CloseIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            />
          );
        })}
      </Box>
    )}
    MenuProps={{
      PaperProps: { style: { maxHeight: 300 } },
    }}
  >
    {(!selectedOrganization || !formData.branch) ? (
      <MenuItem disabled>
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', width: '100%' }}>
          ⚠️ Please select organization and branch first to see available courses
        </Typography>
      </MenuItem>
    ) : filteredCourses.length === 0 ? (
      <MenuItem disabled>
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', width: '100%' }}>
          📭 No courses available for selected organization and branch
        </Typography>
      </MenuItem>
    ) : (
      filteredCourses.map((course) => (
        <MenuItem key={course.id} value={course.id}>
          <Checkbox checked={selectedCourses.indexOf(course.id) > -1} />
          {course.course_name || `Course ${course.id}`}
        </MenuItem>
      ))
    )}
    {filteredCourses.length > 0 && (
      <ListSubheader sx={{
        position: 'sticky',
        bottom: 0,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        p: 1,
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => setCourseDropdownOpen(false)}
        >
          OK
        </Button>
      </ListSubheader>
    )}
  </Select>
  {selectedOrganization && formData.branch && filteredCourses.length === 0 && (
    <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.warning.main }}>
      No courses available for this organization and branch combination
    </Typography>
  )}
  {selectedOrganization && formData.branch && filteredCourses.length > 0 && (
    <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.info.main }}>
      Showing {filteredCourses.length} course(s) available for selected organization and branch
    </Typography>
  )}
</FormControl>

          {/* Batches Multi-Select — same as AddAdmission */}
          <FormControl fullWidth sx={{ mb: 3 }} required>
            <InputLabel
              shrink={selectedBatches.length > 0}
              id="batches-label"
              sx={{ color: selectedCourses.length === 0 ? theme.palette.text.disabled : theme.palette.text.primary }}
            >
              Batches {selectedCourses.length === 0 && "(Select courses first)"}
            </InputLabel>
            <Select
              labelId="batches-label"
              multiple
              value={selectedBatches}
              onChange={handleBatchChange}
              label="Batches"
              disabled={selectedCourses.length === 0}
              open={batchDropdownOpen}
              onOpen={() => setBatchDropdownOpen(true)}
              onClose={() => setBatchDropdownOpen(false)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const batch = batches.find(b => b.id === value);
                    const courseName = getCourseName(batch?.course);
                    return (
                      <Tooltip key={value} title={`Course: ${courseName}`} arrow>
                        <Chip
                          label={batch?.batch_name || batch?.name || `Batch ${value}`}
                          size="small"
                          onDelete={() => {
                            setSelectedBatches(selectedBatches.filter(id => id !== value));
                          }}
                          deleteIcon={<CloseIcon />}
                          icon={<span>📚</span>}
                          sx={{
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.contrastText,
                            '&:hover': { backgroundColor: theme.palette.secondary.dark },
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: { style: { maxHeight: 400 } },
                disablePortal: true,
              }}
            >
              {selectedCourses.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', width: '100%' }}>
                    ⚠️ Please select at least one course first to see available batches
                  </Typography>
                </MenuItem>
              ) : filteredBatches.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', width: '100%' }}>
                    📭 No batches available for selected courses
                  </Typography>
                </MenuItem>
              ) : (
                <>
                  {Object.entries(getBatchesGroupedByCourse()).map(([courseName, courseBatches]) => (
                    <Fragment key={courseName}>
                      <ListSubheader sx={{
                        backgroundColor: theme.palette.primary.main + '10',
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        lineHeight: '36px',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>🎓</span>
                          <span>{courseName}</span>
                          <Badge badgeContent={courseBatches.length} color="primary" sx={{ ml: 1 }} />
                        </Box>
                      </ListSubheader>
                      {courseBatches.map((batch) => (
                        <MenuItem
                          key={batch.id}
                          value={batch.id}
                          sx={{ pl: 4 }}
                          onClick={(e) => {
                            // Prevent event propagation
                            e.stopPropagation();
                            const currentIndex = selectedBatches.indexOf(batch.id);
                            const newChecked = [...selectedBatches];

                            if (currentIndex === -1) {
                              newChecked.push(batch.id);
                            } else {
                              newChecked.splice(currentIndex, 1);
                            }

                            setSelectedBatches(newChecked);
                          }}
                        >
                          <Checkbox
                            checked={selectedBatches.indexOf(batch.id) > -1}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {batch.batch_name || batch.name || `Batch ${batch.id}`}
                            </Typography>
                            {batch.batch_code && (
                              <Typography variant="caption" color="text.primary">
                                Code: {batch.batch_code}
                              </Typography>
                            )}
                            {batch.start_date && (
                              <Typography variant="caption" color="text.primary">
                                Starts: {new Date(batch.start_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Fragment>
                  ))}

                  {/* OK Button to close dropdown */}
                  <ListSubheader sx={{
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: 1,
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setBatchDropdownOpen(false)}
                    >
                      OK
                    </Button>
                  </ListSubheader>
                </>
              )}
            </Select>
          </FormControl>

          {/* Selected Batches Summary — same as AddAdmission */}
          {selectedBatches.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: theme.palette.primary.main }}>
                Selected Batches Summary:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedBatches.map(batchId => {
                  const batch = batches.find(b => b.id === batchId);
                  const courseName = getCourseName(batch?.course);
                  return (
                    <Chip
                      key={batchId}
                      label={`${courseName} → ${batch?.batch_name || batch?.name || `Batch ${batchId}`}`}
                      onDelete={() => setSelectedBatches(selectedBatches.filter(id => id !== batchId))}
                      deleteIcon={<CloseIcon />}
                      sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        '&:hover': { backgroundColor: theme.palette.secondary.dark },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip
              title={
                !formData.admissionCode && 'Enter Admission Code' ||
                !formData.candidateName && 'Enter Candidate Name' ||
                !formData.mobileNo && 'Enter Mobile No.' ||
                !formData.gender && 'Select Gender' ||
                !formData.email && 'Enter Email' ||
                !formData.address && 'Enter Address' ||
                !formData.status && 'Select Status' ||
                !formData.branch && 'Select Branch' ||
                (selectedCourses.length === 0 && 'Select at least one Course') ||
                (selectedBatches.length === 0 && 'Select at least one Batch')
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  {isSubmitting ? 'UPDATING...' : 'UPDATE'}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
              BACK
            </Button>
          </Box>
        </form>

        {/* Success Modal */}
        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
                Admission Updated
              </Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>Admission details have been updated successfully.</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/admission');
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>

      {/* Snackbar - Fixed syntax error here */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditAdmission;